import { getPasskey, type LinkStatus, type SecondFactor } from '@central/api';
import { color, isApiError } from '@central/ui';
import { Button, Loader, PasswordInput, TextInput } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useApi } from '../api';
import { ErrorText, Panel, SignInPage } from '../components/signInParts';
import {
  amberButton,
  bodyText,
  codeInputStyles,
  inputStyles,
  quietButton,
} from '../components/signInStyles';
import { errorMessage } from '../lib/errors';
import { useSignedIn } from '../lib/session';

const noFactor =
  'Your account has no second factor yet. Sign in with your password, add a passkey or an authenticator app, then sign in through single sign-on again.';

/** link_expired is answered by the expired screen, not inline text. */
const expiredError = (e: unknown) => isApiError(e, 'link_expired');

/**
 * Step-up link (`/sign-in/link`): the first SSO sign-in for an existing
 * console account proves the account with its password and a second factor
 * before the SSO identity is linked to it. The pending link lives in the
 * server's __Host-central_console_sso cookie; GET /auth/link reports how far
 * it got.
 */
export function LinkSso() {
  const api = useApi();
  const status = useQuery({
    queryKey: ['auth', 'link'],
    queryFn: () => api.auth.link.status(),
    retry: false,
    staleTime: Infinity,
  });
  return (
    <SignInPage>
      {status.isPending ? (
        <Loader size="sm" aria-label="Loading" />
      ) : status.error ? (
        isApiError(status.error, 'link_expired') ? (
          <Expired />
        ) : (
          <Panel step={1} of={2} title="Link your single sign-on">
            <ErrorText>{errorMessage(status.error)}</ErrorText>
          </Panel>
        )
      ) : (
        <Steps link={status.data} />
      )}
    </SignInPage>
  );
}

function Expired() {
  return (
    <Panel step={1} of={2} title="This sign-on link has expired">
      <p style={bodyText}>
        Linking has to finish within 10 minutes, in the browser that started it. Sign in through
        single sign-on again to start over.
      </p>
      <Link to="/sign-in" style={{ color: color.signInLink, fontSize: 14 }}>
        Back to sign in
      </Link>
    </Panel>
  );
}

function Steps({ link }: { link: LinkStatus }) {
  const [methods, setMethods] = useState<SecondFactor[] | undefined>(
    link.passwordVerified ? link.methods : undefined,
  );
  const [expired, setExpired] = useState(false);
  if (expired) return <Expired />;
  const onError = (e: unknown) => {
    if (isApiError(e, 'link_expired')) setExpired(true);
    else if (isApiError(e, 'password_required')) setMethods(undefined);
  };
  const intro = (
    <p style={bodyText}>
      You signed in through {link.provider} as <b>{link.email}</b> for the first time. Confirm it’s
      your console account once; after that, {link.provider} signs you straight in.
    </p>
  );
  return methods === undefined ? (
    <PasswordStep intro={intro} onVerified={setMethods} onError={onError} />
  ) : (
    <FactorStep intro={intro} methods={methods} onError={onError} />
  );
}

function PasswordStep({
  intro,
  onVerified,
  onError,
}: {
  intro: ReactNode;
  onVerified: (m: SecondFactor[]) => void;
  onError: (e: unknown) => void;
}) {
  const api = useApi();
  const [password, setPassword] = useState('');
  const verify = useMutation({
    mutationFn: () => api.auth.link.password({ password }),
    onSuccess: (r) => onVerified(r.methods),
    onError: (e) => onError(e),
  });
  const err = verify.error;
  return (
    <Panel step={1} of={2} title="Link your single sign-on" onSubmit={() => verify.mutate()}>
      {intro}
      <PasswordInput
        label="Password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.currentTarget.value)}
        required
        styles={inputStyles}
      />
      <ErrorText>
        {err && !expiredError(err)
          ? isApiError(err, 'second_factor_required')
            ? noFactor
            : errorMessage(err)
          : undefined}
      </ErrorText>
      <Button type="submit" fullWidth h={44} loading={verify.isPending} style={quietButton}>
        Continue
      </Button>
    </Panel>
  );
}

function FactorStep({
  intro,
  methods,
  onError,
}: {
  intro: ReactNode;
  methods: SecondFactor[];
  onError: (e: unknown) => void;
}) {
  const api = useApi();
  const signedIn = useSignedIn();
  const [code, setCode] = useState('');
  const totp = useMutation({
    mutationFn: () => api.auth.link.totp({ code: code.replace(/\s/g, '') }),
    onSuccess: signedIn,
    onError: (e) => onError(e),
  });
  const passkey = useMutation({
    mutationFn: async () => {
      const { publicKey } = await api.auth.link.webauthnBegin();
      const credential = await getPasskey(publicKey);
      return api.auth.link.webauthnFinish({ credential });
    },
    onSuccess: signedIn,
    onError: (e) => onError(e),
  });
  const error = totp.error ?? passkey.error;
  const canTotp = methods.includes('totp');
  return (
    <Panel
      step={2}
      of={2}
      title="Link your single sign-on"
      onSubmit={() => (canTotp ? totp.mutate() : undefined)}
    >
      {intro}
      {methods.length === 0 && <ErrorText>{noFactor}</ErrorText>}
      {methods.includes('webauthn') && (
        <Button
          type="button"
          fullWidth
          h={44}
          style={amberButton}
          loading={passkey.isPending}
          onClick={() => passkey.mutate()}
        >
          Use a passkey
        </Button>
      )}
      {canTotp && (
        <>
          <TextInput
            label="Code from your authenticator app"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
            required
            styles={codeInputStyles}
          />
          <Button type="submit" fullWidth h={44} loading={totp.isPending} style={quietButton}>
            Verify and link
          </Button>
        </>
      )}
      <ErrorText>{error && !expiredError(error) ? errorMessage(error) : undefined}</ErrorText>
    </Panel>
  );
}
